import React, { useState, useEffect } from 'react';
import { ArrowLeft, ShieldCheck, PlayCircle, Loader2 } from 'lucide-react';

/**
 * BuilderHeader - Header bar for the automation builder
 * 
 * Props:
 * - currentAutomation: The automation being edited
 * - onBack: Navigate back to library
 * - onSave: Save the current automation
 * - onNameChange: Update automation name
 * - isSaving: Whether save is in progress
 * - onToggleLeadsPanel: Toggle leads monitor panel
 * - onShowGuardrails: Show guardrails modal
 * - showLeadsPanel: Whether leads panel is visible
 * - isMobile: Mobile layout flag
 */
const BuilderHeader = ({
  currentAutomation,
  onBack,
  onSave,
  onNameChange,
  isSaving,
  onToggleLeadsPanel,
  onShowGuardrails,
  onTestRun,
  onStartAutomation,
  onStopAutomation,
  canStartAutomation = false,
  startDisabledReason = 'Add steps first',
  runStats = {},
  showLeadsPanel,
  isMobile,
  isReadOnly = false
}) => {
  // Local state for name editing - start empty, hydrate from Firestore
  const [name, setName] = useState('');
  const [lastSaved, setLastSaved] = useState('just now');
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  // Track which automation's name has been loaded into the input
  const [loadedAutomationId, setLoadedAutomationId] = useState(null);

  // Debug: log mount/unmount for lifecycle tracing
  useEffect(() => {
    console.log('[BuilderHeader] mounted', { currentAutomationId: currentAutomation?.id });
    return () => console.log('[BuilderHeader] unmounted');
  }, []);

  // Sync name from Firestore when automation data arrives
  // Only load when a real name exists AND it's a different automation id
  useEffect(() => {
    console.log('[BuilderHeader] effect run', { id: currentAutomation?.id, name: currentAutomation?.name, loadedAutomationId });
    if (!currentAutomation?.name) {
      console.log('[BuilderHeader] no name in currentAutomation, skipping hydrate');
      return;
    }

    // If this is a new automation (id changed), hydrate the input with Firestore value.
    // This avoids overwriting any user edits for the currently-loaded automation.
    if (loadedAutomationId !== currentAutomation.id) {
      console.log('[BuilderHeader] hydrating name from Firestore', currentAutomation.name);
      setName(currentAutomation.name);
      setLoadedAutomationId(currentAutomation.id);
    } else {
      console.log('[BuilderHeader] already loaded this automation, not hydrating', loadedAutomationId);
    }
  }, [currentAutomation?.id, currentAutomation?.name, loadedAutomationId]);

  // Debug: log local name changes
  useEffect(() => {
    console.log('[BuilderHeader] local name changed', name);
  }, [name]);

  // Debug: log at render time to confirm what will be displayed
  console.log('[BuilderHeader] render', { name, currentAutomationId: currentAutomation?.id, currentAutomationName: currentAutomation?.name });

  const handleNameChange = (e) => {
    if (isReadOnly) return;
    const newName = e.target.value;
    console.log('[BuilderHeader] user changed name', newName);
    setName(newName);
    if (onNameChange) {
      onNameChange(newName);
    }
  };

  const handleSaveAndClose = async () => {
    if (onSave) {
      await onSave();
      setLastSaved('just now');
    }
    onBack();
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave();
      setLastSaved('just now');
    }
  };

  const isActive = currentAutomation?.status === 'ACTIVE';

  const handleStart = async () => {
    if (!onStartAutomation || !canStartAutomation || isStarting) return;
    setIsStarting(true);
    try {
      await onStartAutomation();
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    if (!onStopAutomation || isStopping) return;
    setIsStopping(true);
    try {
      await onStopAutomation();
    } finally {
      setIsStopping(false);
    }
  };

  if (isMobile) {
    return (
      <>
        <header className="h-14 bg-white border-b border-notion-border px-4 flex items-center justify-between z-30 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-notion-sidebar rounded-lg text-stone-400">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col">
              <input
                type="text"
                value={name}
                onChange={handleNameChange}
                readOnly={isReadOnly}
                className={`font-bold text-notion-text text-sm outline-none bg-transparent w-40 ${isReadOnly ? 'cursor-default text-stone-400' : ''}`}
                placeholder="Automation name"
              />
              <span className="text-[10px] text-stone-400">
                {isSaving ? 'Saving...' : `Last saved ${lastSaved}`}
              </span>
            </div>
          </div>
          <button
            onClick={handleSaveAndClose}
            disabled={isSaving || isReadOnly}
            className={`bg-black text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 flex items-center gap-2 ${isReadOnly ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            {isSaving && <Loader2 className="w-3 h-3 animate-spin" />}
            Save
          </button>
        </header>

        <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto bg-stone-50/50">
          <button
            onClick={onToggleLeadsPanel}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-white border border-notion-border rounded-lg text-xs font-medium text-stone-600 shadow-sm"
          >
            <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></div>
            Monitor
            {(runStats.running > 0 || runStats.paused > 0) && (
              <span className="flex items-center gap-1 ml-0.5 text-[10px]">
                {runStats.running > 0 && <span className="text-emerald-600">{runStats.running}</span>}
                {runStats.paused > 0 && <span className="text-amber-600">{runStats.paused}</span>}
              </span>
            )}
          </button>
          <button
            onClick={onShowGuardrails}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-white border border-notion-border rounded-lg text-xs font-medium text-stone-600 shadow-sm"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-orange-500" /> Guardrails
          </button>
          <button
            onClick={onTestRun}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-white border border-notion-border rounded-lg text-xs font-medium text-stone-600 shadow-sm"
          >
            <PlayCircle className="w-3.5 h-3.5" /> Test Run
          </button>
          {!isActive ? (
            <button
              onClick={handleStart}
              disabled={!canStartAutomation || isStarting}
              title={!canStartAutomation ? startDisabledReason : 'Start Automation'}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm ${canStartAutomation && !isStarting
                ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
            >
              {isStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Start
            </button>
          ) : (
            <button
              onClick={handleStop}
              disabled={isStopping}
              title="Stop Automation"
              className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
            >
              {isStopping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Stop
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <header className="h-16 bg-white border-b border-notion-border px-6 flex items-center justify-between z-30 shadow-sm flex-shrink-0">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 hover:bg-notion-sidebar rounded-lg text-stone-400 hover:text-stone-700 transition">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="h-6 w-px bg-stone-200"></div>
        <div className="flex flex-col">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            readOnly={isReadOnly}
            className={`font-bold text-notion-text text-sm outline-none hover:bg-stone-50 rounded px-2 -ml-2 transition w-64 bg-transparent ${isReadOnly ? 'cursor-default text-stone-400 hover:bg-transparent' : ''}`}
            placeholder="Automation name"
          />
          <span className="text-[10px] text-stone-400 px-2 -ml-2">
            {isSaving ? 'Saving...' : `Last saved ${lastSaved}`}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleLeadsPanel}
          className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 border border-notion-border rounded-lg text-xs font-medium text-stone-600 hover:bg-stone-100 transition shadow-sm"
        >
          <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></div>
          Monitor Leads
          {(runStats.running > 0 || runStats.paused > 0 || runStats.completed > 0) && (
            <span className="flex items-center gap-1.5 ml-1 text-[10px]">
              {runStats.running > 0 && (
                <span className="text-emerald-600" title="Running">{runStats.running} running</span>
              )}
              {runStats.paused > 0 && (
                <span className="text-amber-600" title="Paused">{runStats.paused} paused</span>
              )}
              {runStats.completed > 0 && (
                <span className="text-stone-400" title="Completed">{runStats.completed} done</span>
              )}
            </span>
          )}
        </button>
        <div className="h-6 w-px bg-stone-200"></div>
        <button
          onClick={onShowGuardrails}
          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-notion-border rounded-lg text-xs font-medium text-stone-600 hover:bg-notion-sidebar transition shadow-sm"
        >
          <ShieldCheck className="w-3.5 h-3.5 text-orange-500" /> Guardrails On
        </button>
        <button
          onClick={onTestRun}
          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-notion-border rounded-lg text-xs font-medium text-stone-600 hover:bg-notion-sidebar transition shadow-sm"
        >
          <PlayCircle className="w-3.5 h-3.5" /> Test Run
        </button>
        {!isActive ? (
          <button
            onClick={handleStart}
            disabled={!canStartAutomation || isStarting}
            title={!canStartAutomation ? startDisabledReason : 'Start Automation'}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition shadow-sm ${canStartAutomation && !isStarting
              ? 'bg-emerald-500 text-white hover:bg-emerald-600'
              : 'bg-stone-100 text-stone-400 cursor-not-allowed'
              }`}
          >
            {isStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Start
          </button>
        ) : (
          <button
            onClick={handleStop}
            disabled={isStopping}
            title="Stop Automation"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition shadow-sm bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
          >
            {isStopping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Stop
          </button>
        )}
        <button
          onClick={handleSaveAndClose}
          disabled={isSaving || isReadOnly}
          className={`bg-black text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:bg-stone-800 transition shadow-sm disabled:opacity-50 flex items-center gap-2 ${isReadOnly ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          {isSaving && <Loader2 className="w-3 h-3 animate-spin" />}
          Save & Close
        </button>
      </div>
    </header>
  );
};

export default BuilderHeader;
